import { Component, OnInit } from "@angular/core";
import { Guid } from "guid-typescript";
import { ErrorModalHelperService } from "../../_helpers/error-modal-helper/error-modal-helper.service";
import { FeatureHelperService } from "../../_helpers/feature-helper/feature-helper.service";
import { DigitalClassroomAccess } from "../../_models/digital-classroom/digital-classroom-access.model";
import { LaunchVideoLibrary } from "../../_models/digital-classroom/digital-classroom-launch-library.model";
import { PlacementService } from "../../_services";

/** The all-zero GUID, as the wire string the API binds. See the note at its use site. */
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/**
 * Landing-page tile that opens the Digital Classroom video library directly.
 *
 * Before this, the only route into the library was the "Launch Video Library" button inside the
 * student modal and the tutor-report flows — all of which need a placement, and in practice a
 * session, to reach. That left recordings effectively unreachable whenever nothing was due, which
 * is the gap this closes.
 *
 * Because there is no placement in this context there is no room to scope to, so this uses the
 * library's super-user path (ClassroomLaunchRequest.IsSuperUser) — the same mechanism the admin
 * button on the Online Lesson Overview dashboard already uses for its context-free launch. That
 * shows the whole library — every room's recordings — so the tile renders only for users with the
 * VideoLibraryAllRooms feature. LaunchVideoLibrary is not enough: it grants only the library for
 * sessions the user took part in, opened from a room context (tutor reports, learner record).
 */
@Component({
  selector: "video-library-tile",
  templateUrl: "./video-library-tile.component.html",
  styleUrls: ["./video-library-tile.component.css"],
  standalone: false
})
export class VideoLibraryTileComponent implements OnInit {
  /** Whether the current user may open the whole-library view. Drives rendering of the tile. */
  public canLaunch: boolean = false;

  /** Guards against double-launch while the access token is being minted. */
  public launching: boolean = false;

  constructor(
    private featureHelperService: FeatureHelperService,
    private placementService: PlacementService,
    private errorModalHelper: ErrorModalHelperService
  ) {}

  ngOnInit(): void {
    this.canLaunch = this.featureHelperService.hasFeature("VideoLibraryAllRooms");
  }

  public launchVideoLibrary(): void {
    if (this.launching) {
      return;
    }
    this.launching = true;

    const request: LaunchVideoLibrary = {
      // Unused on the super-user path — the token is minted for the user, not a room — but the
      // contract types it as a Guid, so send an empty one rather than null.
      //
      // Sent as a plain STRING, not a Guid object. JSON.stringify(Guid.createEmpty()) produces
      // {"value":"00000000-..."}, which ASP.NET cannot bind to a Guid parameter: the request 400s
      // during model binding, before a single line of the endpoint runs. Every other caller passes
      // the string the API already gave it (placement.digitalClassroomId), so this is the only place
      // that ever constructed one — the model's `Guid` type is nominal, the wire format is a string.
      digitalClassroomId: EMPTY_GUID as unknown as Guid,
      isSuperUser: true,
      tutorId: 0,
      clientId: 0,
      isStudent: false
    };

    this.placementService.launchVideoLibrary(request).subscribe(
      (access: DigitalClassroomAccess) => {
        this.launching = false;
        // Opened via setTimeout for the same reason as every other launch in this app: the popup
        // blocker is friendlier to a window.open that is not inside the subscribe's microtask.
        setTimeout(() => {
          window.open(access.accessToken, "_blank");
        }, 0);
      },
      error => {
        this.launching = false;
        const message = error.status === 401 ? "Unauthorized" : error.message;
        this.errorModalHelper.show(message, error);
      }
    );
  }
}
